import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { LogOut, ChevronDown, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import { sidebarGroups, peopleSpaceConfig } from '../spaces/people-space/config';
import { cn } from '../shared';
import type { SpaceNavGroup, SpaceNavItem } from '../shared';
import type { ReactNode } from 'react';

/**
 * The People Space left nav — collapsed rail and expanded tree.
 *
 * Split out of App.tsx, which was at the 500-line ceiling. It owns its own
 * collapsed-state persistence and the RBAC visibility filter; App.tsx keeps the
 * routing and provider wiring.
 */
const SIDEBAR_COLLAPSED_KEY = 'people-space-sidebar-collapsed';
function loadSidebarCollapsed(): boolean {
  try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
}

export function Sidebar() {
  const { user, signOut } = useAuth();
  const { permissions, isCoreAdmin } = useRBAC();
  const navigate = useNavigate();
  const location = useLocation();

  // Hide nav items gated behind an RBAC permission the user lacks. Items with
  // no `requiredPermission` are visible to everyone. This check is STRICT — it
  // uses the resolved permission set directly rather than the audit-flag-aware
  // `hasPermission`, so confidential entries (e.g. Absence Insights) stay hidden
  // from non-members even while org-wide RBAC enforcement is still audit-only.
  const canSee = useCallback(
    (item: { requiredPermission?: string | readonly string[] }) =>
      !item.requiredPermission ||
      isCoreAdmin ||
      permissions.has('*') ||
      // A list means ANY of them — the Org Planning Overview opens for two audiences.
      (typeof item.requiredPermission === 'string'
        ? permissions.has(item.requiredPermission)
        : item.requiredPermission.some((permission) => permissions.has(permission))),
    [permissions, isCoreAdmin]
  );
  const navItems = peopleSpaceConfig.navItems.filter(canSee);
  const groups = sidebarGroups
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);

  const [isCollapsed, setIsCollapsed] = useState(loadSidebarCollapsed);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Cmd+B to toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') { e.preventDefault(); toggleCollapsed(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleCollapsed]);

  const activeGroupId = groups.find((g) =>
    g.items.some(
      (item) =>
        location.pathname === item.path ||
        (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
    )
  )?.id;

  const [manuallyOpened, setManuallyOpened] = useState<Record<string, boolean>>({});

  const openGroups = Object.fromEntries(
    groups.map((g) => [
      g.id,
      manuallyOpened[g.id] ?? g.id === activeGroupId,
    ])
  );

  const toggleGroup = (id: string) => {
    setManuallyOpened((prev) => ({ ...prev, [id]: !openGroups[id] }));
  };

  const isActivePath = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));

  // One group-child button, shared by direct group items and nested subgroup items.
  const renderChild = (item: SpaceNavItem) => {
    const ItemIcon = item.icon;
    const isActive = isActivePath(item.path);
    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm transition-colors text-left',
          isActive
            ? 'bg-zinc-100 text-zinc-900 font-medium'
            : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 font-normal'
        )}
      >
        <ItemIcon className="w-3.5 h-3.5 shrink-0" />
        {item.name}
      </button>
    );
  };

  // The subgroup an item belongs to AT a level: walk up from its own subgroup until the one whose
  // parent is `level` (undefined = the group itself). Null = the item is a direct child of `level`;
  // undefined = it does not live under `level` at all.
  const subgroupAt = (group: SpaceNavGroup, item: SpaceNavItem, level: string | undefined) => {
    if (item.subgroup === level) return null;
    let id = item.subgroup;
    const seen = new Set<string>();
    while (id && !seen.has(id)) {
      seen.add(id);
      const sub = group.subgroups?.find((s) => s.id === id);
      if (!sub) return level === undefined ? id : undefined; // no metadata: top level only
      if (sub.parent === level) return sub.id;
      id = sub.parent;
    }
    return undefined;
  };

  // One level of a group (the group itself, or a subgroup): its own items in config order, and
  // each child subgroup folded into one collapsible sub-section at the position of its first item.
  // A nested folder (`parent`, e.g. Talent Sourcing in Candidate Communication) comes after the
  // parent's own items.
  const renderLevel = (group: SpaceNavGroup, level: string | undefined): ReactNode[] => {
    const seen = new Set<string>();
    const own: ReactNode[] = [];
    const folders: ReactNode[] = [];
    for (const item of group.items) {
      const at = subgroupAt(group, item, level);
      if (at === undefined) continue;
      if (at === null) {
        own.push(renderChild(item));
        continue;
      }
      if (seen.has(at)) continue;
      seen.add(at);
      const sub = group.subgroups?.find((s) => s.id === at);
      const subItems = group.items.filter((i) => subgroupAt(group, i, at) !== undefined);
      // No metadata for the subgroup → degrade to flat items.
      if (!sub) {
        subItems.forEach((i) => own.push(renderChild(i)));
        continue;
      }
      const SubIcon = sub.icon;
      const subKey = `sub:${group.id}:${sub.id}`;
      const activeInside = subItems.some((i) => isActivePath(i.path));
      const subOpen = manuallyOpened[subKey] ?? activeInside;
      (level !== undefined ? folders : own).push(
        <div key={subKey}>
          <button
            onClick={() => setManuallyOpened((prev) => ({ ...prev, [subKey]: !subOpen }))}
            aria-expanded={subOpen}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors text-left',
              activeInside && !subOpen
                ? 'text-zinc-900 bg-zinc-50'
                : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
            )}
          >
            <SubIcon className="w-3.5 h-3.5 shrink-0 text-zinc-500" />
            <span className="flex-1">{sub.name}</span>
            <ChevronDown
              className={cn('w-3 h-3 text-zinc-400 transition-transform duration-200', subOpen && 'rotate-180')}
            />
          </button>
          {subOpen && (
            <div className="mt-0.5 ml-3 pl-3 border-l border-zinc-100 space-y-0.5">
              {renderLevel(group, sub.id)}
            </div>
          )}
        </div>
      );
    }
    return [...own, ...folders];
  };

  // ── Collapsed rail ──────────────────────────────────────────────────────────
  if (isCollapsed) {
    // All nav items flattened: ungrouped + one icon per group (uses active child if any)
    const ungrouped = navItems.filter((item) => !item.group);
    return (
      <aside className="flex flex-col w-14 shrink-0 h-screen bg-white border-r border-zinc-200">
        {/* Toggle button */}
        <div className="flex items-center justify-center h-[53px] border-b border-zinc-100">
          <button
            onClick={toggleCollapsed}
            title="Expand sidebar (⌘B)"
            className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors"
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        </div>

        {/* Nav icons */}
        <nav className="flex-1 overflow-y-auto flex flex-col items-center py-3 gap-1">
          {ungrouped.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                title={item.name}
                className={cn(
                  'w-9 h-9 flex items-center justify-center rounded-lg transition-colors',
                  isActive ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-400 hover:bg-zinc-50 hover:text-zinc-700'
                )}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}

          <div className="w-6 h-px bg-zinc-100 my-1" />

          {groups.map((group) => {
            const GroupIcon = group.icon;
            const hasActiveItem = group.items.some(
              (item) =>
                location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
            );
            // Find active child icon if any
            const activeChild = group.items.find(
              (item) =>
                location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
            );
            const DisplayIcon = activeChild?.icon ?? GroupIcon;
            return (
              <button
                key={group.id}
                onClick={() => { toggleCollapsed(); }}
                title={group.name}
                className={cn(
                  'w-9 h-9 flex items-center justify-center rounded-lg transition-colors',
                  hasActiveItem ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-400 hover:bg-zinc-50 hover:text-zinc-700'
                )}
              >
                <DisplayIcon className="w-4 h-4" />
              </button>
            );
          })}
        </nav>

        {/* User avatar */}
        {user && (
          <div className="flex flex-col items-center py-3 border-t border-zinc-100 gap-1">
            {user.photoURL
              ? <img src={user.photoURL} alt="" className="w-6 h-6 rounded-full" />
              : <div className="w-6 h-6 rounded-full bg-zinc-200" />
            }
            <button
              onClick={signOut}
              title="Sign out"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </aside>
    );
  }

  // ── Expanded sidebar ────────────────────────────────────────────────────────
  return (
    <aside className="flex flex-col w-60 shrink-0 h-screen bg-white border-r border-zinc-200">
      {/* Logo + collapse button */}
      <div className="flex items-center gap-2 px-4 py-4 border-b border-zinc-100">
        <span className="text-black font-black text-base tracking-[0.04em]">FINN</span>
        <span className="text-zinc-300 font-light text-sm">|</span>
        <span className="text-sm font-semibold text-zinc-500 whitespace-nowrap">People Space</span>
        <button
          onClick={toggleCollapsed}
          title="Collapse sidebar (⌘B)"
          className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors shrink-0 ml-auto"
        >
          <PanelLeftClose className="w-4 h-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {/* Ungrouped items (e.g. Home) */}
        {navItems
          .filter((item) => !item.group)
          .map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                  isActive
                    ? 'bg-zinc-100 text-zinc-900'
                    : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {item.name}
              </button>
            );
          })}

        {/* Divider before groups */}
        <div className="pt-2 pb-1 px-3">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Spaces</span>
        </div>

        {groups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = openGroups[group.id] ?? false;
          const hasActiveItem = group.items.some(
            (item) =>
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
          );

          return (
            <div key={group.id}>
              <button
                onClick={() => toggleGroup(group.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors text-left',
                  hasActiveItem && !isOpen
                    ? 'text-zinc-900 bg-zinc-50'
                    : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
                )}
              >
                <GroupIcon className="w-4 h-4 shrink-0 text-zinc-500" />
                <span className="flex-1">{group.name}</span>
                <ChevronDown
                  className={cn(
                    'w-3.5 h-3.5 text-zinc-400 transition-transform duration-200',
                    isOpen && 'rotate-180'
                  )}
                />
              </button>

              {isOpen && (
                <div className="mt-0.5 ml-3 pl-3 border-l border-zinc-100 space-y-0.5">
                  {renderLevel(group, undefined)}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User */}
      {user && (
        <div className="px-2 py-3 border-t border-zinc-100">
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 truncate">
            {user.photoURL && (
              <img src={user.photoURL} alt="" className="w-5 h-5 rounded-full shrink-0" />
            )}
            <span className="truncate">{user.email}</span>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      )}
    </aside>
  );
}
